// Ruta: packages/auth/src/AuthProvider.tsx
// Provides auth state restoration on mount + wires up ky token callbacks

import React, { createContext, useEffect, type ReactNode } from 'react'
import { setTokenCallbacks } from '@heyshaker/api-client'
import { authApi } from '@heyshaker/api-client'
import { useAuthStore } from './auth-store.js'
import { tokenStorage } from './token-storage.js'
import type { AuthUser } from './types.js'

export const AuthContext = createContext<null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const { setTokens, setAccessToken, clearAuth, setStatus, accessToken } = useAuthStore()

  useEffect(() => {
    // Wire ky interceptors to Zustand store
    setTokenCallbacks({
      getAccessToken: () => useAuthStore.getState().accessToken,
      onUnauthorized: () => {
        clearAuth()
        tokenStorage.clearRefreshToken()
      },
      refreshToken: async () => {
        const refreshToken = await tokenStorage.getRefreshToken()
        if (!refreshToken) {
          clearAuth()
          return null
        }
        try {
          const res = await authApi.refresh({ refresh_token: refreshToken })
          const { access_token: newAccessToken } = res.data
          setAccessToken(newAccessToken)
          return newAccessToken
        } catch {
          clearAuth()
          tokenStorage.clearRefreshToken()
          return null
        }
      },
    })
  }, [setAccessToken, clearAuth])

  // Restore session on mount
  useEffect(() => {
    async function restoreSession(): Promise<void> {
      setStatus('loading')
      const refreshToken = await tokenStorage.getRefreshToken()

      if (!refreshToken) {
        setStatus('unauthenticated')
        return
      }

      try {
        const res = await authApi.refresh({ refresh_token: refreshToken })
        const { access_token: newToken } = res.data

        // Decode user from JWT payload (non-sensitive data)
        const user = decodeUserFromToken(newToken)
        if (!user) {
          setStatus('unauthenticated')
          return
        }

        setTokens(newToken, refreshToken, user)
        await tokenStorage.saveRefreshToken(refreshToken)
      } catch {
        clearAuth()
        tokenStorage.clearRefreshToken()
      }
    }

    if (!accessToken) {
      void restoreSession()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <AuthContext.Provider value={null}>{children}</AuthContext.Provider>
}

// ─── JWT decode (non-cryptographic — for display purposes only) ───────────────

function decodeUserFromToken(token: string): AuthUser | null {
  try {
    const [, payloadB64] = token.split('.')
    if (!payloadB64) return null
    // Pad base64 if necessary
    const padded = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), '=')
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>

    return {
      id: payload['sub'] as string,
      email: payload['email'] as string,
      name: payload['name'] as string,
      role: payload['role'] as string,
    }
  } catch {
    return null
  }
}
